import cn from 'classnames';
import React, { useEffect, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';

import Widgets from './containers/Widgets';
import Overlay from './containers/Overlay';
import Spinner from './containers/Spinner';
import Tracer from './utils/tracer';
import decodeMinifiedStacktrace from './utils/decode-minified-stacktrace';
import apiEventBus, { type EventCallbackMapFactory } from './api-event-bus';
import createQueryConfig from './queries';
import { shouldBlur } from './selectors';
import { PageLayout } from './components/Layouts';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { useAppSelector } from './shared/hooks/use-app-selector';
import { API_EVENTS } from './constants/event-bus-events';
import { NOOP } from './constants';
import { window } from './env/browser';
import type { LoadApp } from './components/AppLoader';
import type { GlobalsTools, AppShellApiClient } from './shared/types';

// `loadApp` is part of the real `tools` object built in src/index.js, but
// isn't modeled on the shared GlobalsTools type since most consumers don't
// need it — same rationale as Main/index.tsx's MainGlobals.
interface AppShellGlobals extends GlobalsTools {
  loadApp: LoadApp;
}

function trackDecodedError(type: string) {
  // ErrorEvent.error / PromiseRejectionEvent.reason are `any` per DOM spec —
  // a throw/reject can be any value. This app assumes micro-apps always
  // throw/reject real Error objects (matching the original code's implicit
  // behavior); the cast below documents that assumption rather than hiding
  // it behind an overclaiming parameter type.
  return (encodedError: unknown): Promise<void> => {
    if (!encodedError) {
      return Promise.resolve();
    }

    return decodeMinifiedStacktrace(encodedError as Error)
      .then((error) => {
        console.error(error);
        const context = error.appName ? { appName: error.appName } : {};

        Tracer.addError(error, {
          customError: { ...context, type, decoded: true },
        });
      })
      .catch(() => {
        console.error(new Error('Decoding error process has failed'));
        console.error(encodedError);
        Tracer.addError(encodedError, {
          customError: { type, decoded: false },
        });
      });
  };
}

const globalErrorHandler = (event: ErrorEvent): Promise<void> => {
  event.preventDefault();

  return trackDecodedError('error')(event.error);
};

const globalPromiseRejectionHandler = (
  event: PromiseRejectionEvent
): Promise<void> => {
  event.preventDefault();

  return trackDecodedError('unhandled_promise_rejection')(event.reason);
};

function eventsMap(
  queryClient: QueryClient,
  apiClient: AppShellApiClient
): EventCallbackMapFactory {
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { token } = API_EVENTS;

  return (withCallbacks) => ({
    [token.load]: withCallbacks(
      token.success,
      token.error
    )(function requestToken() {
      return queryClient.fetchQuery(queryConfig.authToken());
    }),
  });
}

interface TracerActionEvent {
  payload: { actionName: string; payload: Record<string, unknown> };
  metadata: Record<string, unknown>;
}

function DeprecatedOverlay({
  tools,
  children,
}: {
  tools: AppShellGlobals;
  children: ReactNode;
}) {
  const currentOverlay = useAppSelector((state) => state.currentOverlay);
  const isBlurred = useAppSelector(shouldBlur);

  const overlayBlurEffect = cn('as-App', {
    'is-blurred': isBlurred,
    'is-temporarilyUnblurred': currentOverlay.isTemporarilyHidden,
  });

  return (
    <>
      <Spinner />
      <div className={overlayBlurEffect}>{children}</div>
      <Overlay {...tools} onProxyAction={NOOP} onInstance={NOOP} />
    </>
  );
}

function AppShell() {
  const tools = useGlobals() as AppShellGlobals;
  const { apiClient, queryClient, eventBus, trackMethod } = tools;

  function subscribeToEventBusEvents() {
    const { unsubscribe } = apiEventBus(eventsMap(queryClient, apiClient))(
      eventBus
    );

    function tracerActionCallback(action: TracerActionEvent): void {
      const { actionName, payload } = action.payload;
      Tracer.addAction(actionName, { payload, metadata: action.metadata });
    }

    eventBus.on('tracer.action', tracerActionCallback);

    return () => {
      unsubscribe();

      eventBus.off('tracer.action', tracerActionCallback);
    };
  }

  function subscribeToMicroAppsErrorHandlers() {
    window.addEventListener(
      'unhandledrejection',
      globalPromiseRejectionHandler
    );

    window.addEventListener('error', globalErrorHandler);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        globalPromiseRejectionHandler
      );

      window.removeEventListener('error', globalErrorHandler);
    };
  }

  useEffect(subscribeToEventBusEvents, []);
  useEffect(subscribeToMicroAppsErrorHandlers, []);

  return (
    <>
      <PageLayout
        apiClient={apiClient}
        eventBus={eventBus}
        trackMethod={trackMethod}
      >
        <div id="asMain" className="as-Main">
          <DeprecatedOverlay tools={tools}>
            <Outlet />
          </DeprecatedOverlay>
        </div>
      </PageLayout>
      <Widgets />
    </>
  );
}

export default AppShell;
